import { useEffect, useState } from "react";
import { EBlogResponse, IBlogGetPage } from "../../models/blog";
import BlogService from "../../services/api/blog.service";
import {
  BlogCard,
  BlogCardDesc,
  BlogCardTitle,
  BlogContent,
  BlogDesc,
  BlogGrid,
  BlogHeader,
  BlogMeta,
  BlogPageWrapper,
  BlogThumb,
  BlogTitle,
} from "../../style/blog.style";
import moment from "moment";
import { envConfig } from "../../config/env.config";
import { Box } from "@mui/material";
import { Helmet } from "react-helmet-async";
import SeoService from "../../services/api/seo.service";
import { ECommonPageSeo } from "../../models/seo";
import FAQ from "../../components/FAQ";
import { useTranslation } from "react-i18next";
import { IVisitRequest } from "../../models/visit";
import VisitService from "../../services/api/visit.service";

export default function BlogPage() {
  const [blogs, setBlogs] = useState<EBlogResponse[]>([]);
  const [currentLocale, setCurrentLocale] = useState<string>("vi");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(12);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const { t, i18n } = useTranslation();

  const [commonPageSeo, setCommonPageSeo] = useState<ECommonPageSeo>();

  // const getDataContent = async (langParam?: string) => {
  //   const lang = (langParam ?? i18n.language)?.toUpperCase();
  //   const mapLang = lang === "VI" ? "VI" : "EN";

  //   const res = await SeoService.getCommonPageSeo("Blog", mapLang);

  //   if (res.success) {
  //     res.data && setCommonPageSeo(res.data);
  //   }
  // };

  // useEffect(() => {
  //   getDataContent(i18n.language);
  // }, [i18n.language]);

  useEffect(() => {
    const createVisit = async () => {
      const data: IVisitRequest = {
        page: "Blog",
        userAgent: navigator.userAgent,
      };

      await VisitService.create(data);
    };

    const init = async () => {
      setLoading(true);

      const lang = i18n.language?.toUpperCase();

      const data: IBlogGetPage = {
        CurrentPage: 1,
        PageSize: pageSize,
        KeySearch: "",
        Language: lang,
      };

      const [blogRes, seoRes] = await Promise.all([
        BlogService.userGetPage(data),
        SeoService.getCommonPageSeo("Blog", lang),
      ]);

      if (blogRes.success && blogRes.items) {
        setBlogs(blogRes.items);
        if (blogRes.items.length < pageSize) setHasMore(false);
      }

      if (seoRes.success && seoRes.data) {
        setCommonPageSeo(seoRes.data);
      }

      setLoading(false);
    };

    init();
    createVisit();
  }, []);

  const getPageBlog = async (page: number) => {
    if (loading || !hasMore) return;

    setLoading(true);

    const data: IBlogGetPage = {
      CurrentPage: page,
      PageSize: pageSize,
      KeySearch: "",
    };

    const res = await BlogService.userGetPage(data);

    if (res.success && res.items) {
      setBlogs((prev) => [...prev, ...res.items]);

      // nếu số item trả về < pageSize → hết data
      if (res.items.length < pageSize) {
        setHasMore(false);
      }
    }

    setLoading(false);
  };

  const handleLoadMore = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    getPageBlog(nextPage);
  };

  // useEffect(() => {
  //   getPageBlog(1);
  // }, []);

  return (
    <>
      <Helmet>
        {/* ===== BASIC SEO ===== */}
        <title>
          {commonPageSeo?.page?.title || "Dịch tài liệu online | MikoTech"}
        </title>

        <meta
          name="description"
          content={
            commonPageSeo?.seo?.description ||
            "Dịch tài liệu online nhanh, chính xác, bảo mật. Hỗ trợ PDF, Word, Excel, PPT, hình ảnh."
          }
        />

        <meta
          name="keywords"
          content={
            commonPageSeo?.seo?.keywords ||
            "dịch tài liệu, dịch file pdf, dịch tiếng anh"
          }
        />

        <link rel="icon" href="/logo/logodocme.svg" />
        <meta name="author" content={commonPageSeo?.seo?.author} />
        {/* <meta name="robots" content="index, follow" /> */}
        <meta
          name="robots"
          content={
            commonPageSeo?.seo?.noIndex ? "noindex, nofollow" : "index, follow"
          }
        />
        {/* ===== CANONICAL ===== */}
        <link
          rel="canonical"
          href={
            commonPageSeo?.seo?.canonical
              ? commonPageSeo?.seo?.canonical
              : "https://docme.vn" + commonPageSeo?.page.url
          }
        />

        {/* ===== OPEN GRAPH ===== */}
        <meta property="og:type" content={commonPageSeo?.seo?.og_type} />
        <meta
          property="og:site_name"
          content={commonPageSeo?.seo?.og_site_name}
        />
        <meta property="og:title" content={commonPageSeo?.seo?.og_title} />
        <meta
          property="og:description"
          content={commonPageSeo?.seo?.og_description}
        />
        <meta
          property="og:url"
          content={commonPageSeo?.seo?.og_url || "https://docme.vn/"}
        />
        <meta property="og:image" content={commonPageSeo?.seo?.og_image_alt} />
        <meta
          property="og:image:width"
          content={commonPageSeo?.seo?.og_image_width}
        />
        <meta
          property="og:image:height"
          content={commonPageSeo?.seo?.og_image_height}
        />
        <meta property="og:locale" content={commonPageSeo?.seo?.og_locale} />

        <meta name="twitter:card" content={commonPageSeo?.seo?.twitter_card} />
        <meta
          name="twitter:title"
          content={commonPageSeo?.seo?.twitter_title}
        />
        <meta
          name="twitter:description"
          content={commonPageSeo?.seo?.twitter_description}
        />
        <meta
          name="twitter:image"
          content={commonPageSeo?.seo?.twitter_image}
        />

        {/* ===== CONTACT / BUSINESS INFO ===== */}
        {commonPageSeo?.seo?.schema && (
          <div
            dangerouslySetInnerHTML={{ __html: commonPageSeo?.seo?.schema }}
          />
        )}
      </Helmet>

      <BlogPageWrapper>
        <BlogHeader>
          <BlogTitle>{t("blog.pageTitle")}</BlogTitle>
          <BlogDesc>{t("blog.pageDescription")}</BlogDesc>
        </BlogHeader>

        <BlogGrid>
          {blogs &&
            blogs.map((blog) => (
              <BlogCard key={blog.id} href={`/${blog.slug}`}>
                <Box
                  sx={{
                    position: "relative",
                    paddingTop: "200px",
                    overflow: "hidden",
                  }}>
                  <BlogThumb src={envConfig.MEDIA_ENDPOINT + blog.image} />
                </Box>
                <BlogContent>
                  <BlogCardTitle>{blog.title}</BlogCardTitle>
                  <BlogCardDesc>{blog.description}</BlogCardDesc>
                  <BlogMeta>
                    <span>Admin</span>
                    <span>
                      {moment(blog.dateTime)
                        .locale(currentLocale)
                        .format("DD/MM/YYYY")}
                    </span>
                  </BlogMeta>
                </BlogContent>
              </BlogCard>
            ))}
        </BlogGrid>

        {/* Load more */}
        {hasMore && (
          <div style={{ textAlign: "center", marginTop: 32 }}>
            <button
              onClick={handleLoadMore}
              disabled={loading}
              style={{
                padding: "12px 32px",
                borderRadius: 8,
                border: "none",
                cursor: "pointer",
                fontWeight: 500,
              }}>
              {loading ? t("common.loading") : t("common.loadMore")}
            </button>
          </div>
        )}

        {!hasMore && (
          <p style={{ textAlign: "center", marginTop: 24, opacity: 0.6 }}>
            {t("blog.noMorePosts")}
          </p>
        )}

        {/* {commonPageSeo?.faqs && commonPageSeo?.faqs.length > 0 && (
          <FAQ faqs={commonPageSeo?.faqs} />
        )} */}
      </BlogPageWrapper>
    </>
  );
}
